import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface CreateFolderModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  folderName: string;
  onFolderNameChange: (value: string) => void;
  validation: { valid: boolean; error: string | null };
  onCreate: () => void;
  isCreating: boolean;
}

export function CreateFolderModal({
  isOpen,
  onOpenChange,
  folderName,
  onFolderNameChange,
  validation,
  onCreate,
  isCreating,
}: CreateFolderModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t('dialog.createFolder')}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-4">
          <div className="relative">
            <input
              type="text"
              value={folderName}
              onChange={e => onFolderNameChange(e.target.value.slice(0, 32))}
              placeholder={t('project.folderName')}
              className={`w-full p-3 text-sm border rounded-lg focus:outline-none focus:ring-1 bg-white ${
                validation.error
                  ? 'border-red-400 focus:ring-red-300'
                  : 'border-gray-400 focus:ring-blue-300'
              }`}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter' && !isCreating && validation.valid) {
                  onCreate();
                }
              }}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {folderName.length}/32
            </span>
          </div>
          {validation.error && (
            <p className="mt-1 text-xs text-red-500">{validation.error}</p>
          )}
        </div>
        <DialogFooter className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              onFolderNameChange('');
            }}
            disabled={isCreating}
            className="px-4 py-2"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onCreate}
            disabled={!validation.valid || isCreating}
            className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
          >
            {isCreating ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                {t('common.saving')}
              </span>
            ) : (
              t('common.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateFolderModal;
